import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { Affiliate } from '../../types';
import { authService } from '../../services/authService';
import {
  adminService,
  ConsentUserDetail,
  ConsentUserRow,
  ConsentUsersResponse,
} from '../../services/adminService';
import { describeConsentAction, describeConsentDevice } from '../../services/consentService';
import { TableSkeleton } from '../Skeleton';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

/**
 * 이용 고지 동의 현황 (2026-11) — 직원마다 동의 상태·마지막 기록, 펼치면 동의 이력(항목·IP·기기)과
 * (Super Admin만) 최근 90일 접속 기록. 판정은 서버(services/consentDocs.decide)가 내려 준 status를 그대로 쓴다.
 * 대상 = 로그인 승인 명단 ∪ 동의 기록이 있는 사람(명단 밖이면 '승인 명단 외' — 퇴사자 등).
 */
type Filter = 'all' | 'required' | 'none' | 'valid';

const STATUS_VIEW: Record<string, { label: string; cls: string }> = {
  valid: { label: '동의 유효', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired: { label: '재확인 필요 · 100일 지남', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  changed: { label: '재확인 필요 · 고지 변경', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  declined: { label: '동의하지 않음', cls: 'bg-brand-50 text-brand-700 border-brand-200' },
  withdrawn: { label: '동의 철회', cls: 'bg-brand-50 text-brand-700 border-brand-200' },
  none: { label: '기록 없음', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  unavailable: { label: '고지 미게시', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const EVENT_LABEL: Record<string, string> = {
  login_success: '로그인',
  login_failure: '로그인 실패',
  login_locked: '로그인 잠김',
  login_denied: '로그인 거부',
  resume: '세션 재개',
  logout: '로그아웃',
};

const fmt = (v: string | null | undefined) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const yn = (v: boolean | undefined) => (v ? '동의' : '거부');

export const ConsentManager: React.FC = () => {
  const [data, setData] = useState<ConsentUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [affiliateId, setAffiliateId] = useState<string | undefined>(undefined);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, ConsentUserDetail | 'loading' | 'error'>>({});

  const load = useCallback(async (aff?: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminService.getConsentUsers(aff);
      setData(r);
      setDetails({});
      setOpenId(null);
    } catch (err: any) {
      setError(err?.message || '동의 현황을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(affiliateId); }, [load, affiliateId]);

  useEffect(() => {
    if (!data?.canManageAllAffiliates) return;
    authService.getAffiliates().then(setAffiliates).catch(() => setAffiliates([]));
  }, [data?.canManageAllAffiliates]);

  const counts = useMemo(() => {
    const c = { valid: 0, required: 0, none: 0 };
    for (const u of data?.users || []) {
      if (u.status === 'valid') c.valid += 1;
      else if (u.status === 'none') c.none += 1;
      else if (u.required) c.required += 1;
    }
    return c;
  }, [data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.users || []).filter((u) => {
      if (filter === 'valid' && u.status !== 'valid') return false;
      if (filter === 'none' && u.status !== 'none') return false;
      if (filter === 'required' && !(u.required && u.status !== 'none')) return false;
      if (!q) return true;
      return [u.userName, u.depart, u.userId].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [data, filter, query]);

  const toggle = async (u: ConsentUserRow) => {
    if (openId === u.userId) {
      setOpenId(null);
      return;
    }
    setOpenId(u.userId);
    if (details[u.userId] && details[u.userId] !== 'error') return;
    setDetails((prev) => ({ ...prev, [u.userId]: 'loading' }));
    try {
      const d = await adminService.getConsentUserDetail(u.userId, data?.affiliateId);
      setDetails((prev) => ({ ...prev, [u.userId]: d }));
    } catch {
      setDetails((prev) => ({ ...prev, [u.userId]: 'error' }));
    }
  };

  const doc = data?.document;

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* 고지문 상태 */}
      <div className="mb-3 p-3 rounded-xl border border-slate-200 bg-slate-50 text-sm">
        {doc ? (
          <p className="text-slate-700">
            게시된 고지 <b>버전 {doc.version}</b> · 시행 {doc.effectiveLabel || doc.effectiveDate} · 재확인 주기 {data?.reagreeDays}일
            {typeof doc.facts?.attachmentRetentionDays === 'number' && (
              <span className="text-slate-500"> · 첨부 보관 기간 {doc.facts.attachmentRetentionDays === 0 ? '무기한' : `${doc.facts.attachmentRetentionDays}일`}</span>
            )}
          </p>
        ) : (
          !loading && <p className="text-slate-700">게시된 이용 고지가 없습니다. 직원에게 동의 창이 뜨지 않습니다</p>
        )}
        {(data?.publishErrors || []).length > 0 && (
          <div className="mt-2 flex items-start gap-2 text-xs text-brand-700">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-medium">고지문 설정 오류 — server/config/consent/chat_notice.json을 고친 뒤 서버를 다시 시작해 주세요</p>
              <ul className="list-disc pl-4 mt-0.5">
                {data!.publishErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* 요약 + 필터 */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {data?.canManageAllAffiliates && affiliates.length > 1 && (
          <select
            value={affiliateId ?? data.affiliateId}
            onChange={(e) => setAffiliateId(e.target.value)}
            aria-label="계열사 선택"
            className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            {affiliates.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        )}
        {([
          ['all', `전체 ${data?.users.length ?? 0}`],
          ['required', `재확인 필요 ${counts.required}`],
          ['none', `기록 없음 ${counts.none}`],
          ['valid', `동의 유효 ${counts.valid}`],
        ] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${filter === key
              ? 'bg-brand-100 border-brand-200 text-brand-700 font-medium'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
          >
            {label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="이름, 부서, 사번으로 찾기"
            aria-label="직원 찾기"
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        <button
          type="button"
          onClick={() => void load(affiliateId)}
          aria-label="새로 고침"
          className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
        >
          <RefreshCw size={16} aria-hidden="true" />
        </button>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded-lg border border-brand-200">{error}</div>
      )}

      {loading && !data ? (
        <TableSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <div className="py-10 text-center">
          <p className="text-sm text-slate-600">표시할 직원이 없습니다</p>
          <p className="text-xs text-slate-400 mt-1">검색어나 필터를 바꿔 보세요</p>
        </div>
      ) : (
        <ul className={`divide-y divide-slate-100 border border-slate-200 rounded-xl bg-white ${ADMIN_LIST_FILL}`}>
          {rows.map((u) => {
            const st = STATUS_VIEW[u.status] || STATUS_VIEW.none;
            const open = openId === u.userId;
            const det = details[u.userId];
            return (
              <li key={u.userId}>
                <button
                  type="button"
                  onClick={() => void toggle(u)}
                  aria-expanded={open}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
                >
                  {open
                    ? <ChevronDown size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
                    : <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden="true" />}
                  <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap items-center gap-x-2">
                      <span className="text-sm font-medium text-slate-800">{u.userName || u.userId}</span>
                      <span className="text-xs text-slate-400">{u.userId}</span>
                      {u.depart && <span className="text-xs text-slate-500">{u.depart}</span>}
                      {!u.approved && <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-1.5">승인 명단 외</span>}
                      {u.departedAt && <span className="text-[11px] text-slate-500 bg-slate-100 rounded px-1.5">퇴사 확인 {fmt(u.departedAt).slice(0, 12)}</span>}
                    </span>
                    {u.latest && (
                      <span className="block text-xs text-slate-500 mt-0.5">
                        마지막 기록: {describeConsentAction(u.latest.action)} · {fmt(u.latest.createdAt)}
                        {u.latest.version != null ? ` · 버전 ${u.latest.version}` : ''} · {describeConsentDevice(u.latest.clientType, u.latest.appVersion)}
                        {u.status === 'valid' && u.validUntil ? ` · ${fmt(u.validUntil).slice(0, 12)}까지` : ''}
                      </span>
                    )}
                  </span>
                  <span className={`shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                </button>
                {open && (
                  <div className="px-4 pb-3 pl-10 text-xs text-slate-600 space-y-3">
                    {det === 'loading' && <p className="text-slate-500">불러오는 중…</p>}
                    {det === 'error' && <p className="text-brand-700">이력을 불러오지 못했습니다.</p>}
                    {det && typeof det === 'object' && (
                      <>
                        <div>
                          <p className="font-medium text-slate-700 mb-1">동의 이력</p>
                          {det.logs.length === 0 ? (
                            <p className="text-slate-500">기록이 없습니다</p>
                          ) : (
                            <ul className="space-y-1">
                              {det.logs.map((l) => (
                                <li key={l.id} className="leading-relaxed">
                                  <span className={l.action === 'accept' ? 'text-emerald-700 font-medium' : 'text-brand-700 font-medium'}>
                                    {describeConsentAction(l.action)}
                                  </span>{' '}
                                  {fmt(l.createdAt)}{l.version != null ? ` · 버전 ${l.version}` : ''}
                                  {l.action !== 'withdraw' && ` · 고지 ${yn(l.items?.notice)} · 수칙 ${yn(l.items?.rules)} · 대화 열람 ${yn(l.items?.audit_view)}`}
                                  {' · '}{describeConsentDevice(l.clientType, l.appVersion)}
                                  {l.ipAddress ? ` · ${l.ipAddress}` : ''}
                                  {l.userAgent && <span className="block text-slate-400 truncate" title={l.userAgent}>{l.userAgent}</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {det.accessLogs && (
                          <div>
                            <p className="font-medium text-slate-700 mb-1">최근 90일 접속 기록</p>
                            {det.accessLogs.length === 0 ? (
                              <p className="text-slate-500">기록이 없습니다</p>
                            ) : (
                              <ul className="space-y-0.5 max-h-60 overflow-y-auto">
                                {det.accessLogs.map((a) => (
                                  <li key={a.id}>
                                    {fmt(a.createdAt)} · {EVENT_LABEL[a.event] || a.event}
                                    {a.code ? ` (${a.code})` : ''} · {describeConsentDevice(a.clientType, a.appVersion)}
                                    {a.ipAddress ? ` · ${a.ipAddress}` : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
